import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, Clock, ChefHat, PackageCheck } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const STAGES = [
  { key: "confirmed", label: "Confirmado", icon: CheckCircle2 },
  { key: "preparing", label: "Preparando", icon: ChefHat },
  { key: "ready", label: "Listo", icon: PackageCheck },
];

export default function OrderStatus() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [payState, setPayState] = useState("checking");

  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        if (sessionId) {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid") setPayState("paid");
          else if (data.payment_status === "failed") setPayState("failed");
          else setPayState("pending");
        }
        const { data: o } = await api.get(`/orders/${orderId}`);
        setOrder(o);
        if ((sessionId && o.payment_status !== "paid") && tries < 10) {
          tries += 1;
          timer = setTimeout(poll, 2000);
        }
      } catch {
        if (tries < 10) { tries += 1; timer = setTimeout(poll, 2000); }
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [orderId, sessionId]);

  const theme = order?.restaurant?.theme;
  const fg = (hsl) => { try { const l = Number(hsl.split(/\s+/)[2].replace("%", "")); return l > 62 ? "0 0% 12%" : "0 0% 100%"; } catch { return "0 0% 100%"; } };
  const themeStyle = useMemo(() => theme ? {
    "--primary": theme.primary_color, "--primary-foreground": fg(theme.primary_color),
    "--accent": theme.accent_color, "--accent-foreground": fg(theme.accent_color),
    "--background": theme.background_color, "--font-heading": `'${theme.font_heading}'`, "--font-body": `'${theme.font_body}'`,
  } : {}, [theme]);

  if (!order) {
    return <div className="min-h-screen flex items-center justify-center bg-neutral-50"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const paid = order.payment_status === "paid";
  const currentStageIdx = STAGES.findIndex((s) => s.key === order.status);

  return (
    <div style={themeStyle} className="min-h-screen font-body bg-background text-neutral-900">
      <div className="max-w-lg mx-auto px-6 py-12">
        <div className="text-center mb-8">
          {paid ? (
            <>
              <div className="w-20 h-20 rounded-full bg-accent/15 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-11 h-11 text-accent" />
              </div>
              <h1 className="font-heading font-black text-3xl" data-testid="order-success-title">¡Pago confirmado!</h1>
              <p className="text-neutral-500 mt-1">Tu pedido en {order.restaurant?.name} está en camino.</p>
            </>
          ) : (
            <>
              <div className="w-20 h-20 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-4">
                {payState === "failed" ? <Clock className="w-10 h-10 text-red-500" /> : <Loader2 className="w-10 h-10 text-primary animate-spin" />}
              </div>
              <h1 className="font-heading font-black text-2xl">{payState === "failed" ? "Pago no completado" : "Procesando pago…"}</h1>
              <p className="text-neutral-500 mt-1">Confirmando la transacción con Stripe.</p>
            </>
          )}
        </div>

        {paid && (
          <div className="rounded-2xl border border-black/5 bg-white p-5 mb-5">
            <div className="flex justify-between mb-5">
              {STAGES.map((s, i) => {
                const done = i <= currentStageIdx;
                return (
                  <div key={s.key} className="flex flex-col items-center gap-1.5 flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${done ? "bg-primary text-primary-foreground" : "bg-neutral-100 text-neutral-400"}`}>
                      <s.icon className="w-5 h-5" />
                    </div>
                    <span className={`text-xs ${done ? "text-neutral-900 font-medium" : "text-neutral-400"}`}>{s.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-black/5 bg-white p-5" data-testid="order-summary">
          <div className="flex justify-between text-sm text-neutral-500 mb-3">
            <span>Pedido #{order.id.slice(0, 8)}</span>
            <span className="capitalize">{order.status}</span>
          </div>
          <div className="space-y-2">
            {order.items.map((it) => (
              <div key={it.id} className="flex justify-between text-sm">
                <span>{it.quantity}× {it.name}</span>
                <span className="tabular-nums">{money(it.line_total)}</span>
              </div>
            ))}
          </div>
          <div className="border-t mt-3 pt-3 flex justify-between font-bold">
            <span>Total</span><span className="tabular-nums">{money(order.total)}</span>
          </div>
        </div>

        <Link to={`/r/${order.restaurant?.slug}`}>
          <Button variant="outline" className="w-full mt-6" data-testid="back-to-store-btn">Volver al menú</Button>
        </Link>
      </div>
    </div>
  );
}
